import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus,
  Search,
  Download,
  MoreHorizontal,
  Copy,
  Trash2,
  Loader2,
  ImageOff,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import {
  apiListProducts,
  apiMeta,
  apiBulkProducts,
  apiDuplicateProduct,
  apiCreateProduct,
  eur,
  errMsg,
  cldThumb,
  CSV_EXPORT_URL,
} from "../adminApi";

function StatusBadge({ status }) {
  const published = status === "PUBLISHED";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        published ? "bg-lime/15 text-lime" : "bg-surface3 text-steel"
      }`}
    >
      {published ? "Pubblicato" : "Bozza"}
    </span>
  );
}

function Thumb({ url, name }) {
  return url ? (
    <img
      src={cldThumb(url, "c_pad,w_120,h_120,b_transparent,f_auto,q_auto")}
      alt={name}
      className="h-10 w-10 rounded-md border border-line object-cover"
    />
  ) : (
    <div className="flex h-10 w-10 items-center justify-center rounded-md border border-line bg-surface3">
      <ImageOff className="h-4 w-4 text-tertiary" />
    </div>
  );
}

export default function Products() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [meta, setMeta] = useState({ brands: [], categories: [] });
  const [error, setError] = useState("");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  const [search, setSearch] = useState("");
  const [brand, setBrand] = useState("all");
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("all");
  const [stock, setStock] = useState("all");

  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = () => {
    setError("");
    const params = {};
    if (search) params.search = search;
    if (brand !== "all") params.brand = brand;
    if (category !== "all") params.category = category;
    if (status !== "all") params.status = status;
    if (stock !== "all") params.stock = stock;
    apiListProducts(params)
      .then((data) => {
        setItems(data);
        setSelected([]);
      })
      .catch((e) => setError(errMsg(e)));
  };

  useEffect(() => {
    apiMeta().then(setMeta).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, brand, category, status, stock]);

  const allChecked = items && items.length > 0 && selected.length === items.length;
  const toggleAll = () => setSelected(allChecked ? [] : items.map((i) => i.id));
  const toggleOne = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const runBulk = async (action, value) => {
    setBusy(true);
    try {
      const res = await apiBulkProducts(selected, action, value);
      if (res.blocked && res.blocked.length) toast.warning(res.message);
      else toast.success("Modifiche applicate");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
      setConfirmDelete(false);
    }
  };

  const duplicate = async (id) => {
    try {
      const p = await apiDuplicateProduct(id);
      toast.success("Prodotto duplicato");
      navigate(`/admin/prodotti/${p.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const createNew = async () => {
    if (!newName.trim()) return;
    try {
      const p = await apiCreateProduct({ name: newName.trim(), status: "DRAFT" });
      toast.success("Prodotto creato");
      navigate(`/admin/prodotti/${p.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const resetFilters = () => {
    setSearch(""); setBrand("all"); setCategory("all"); setStatus("all"); setStock("all");
  };

  const count = items?.length ?? 0;
  const catLabel = useMemo(() => {
    const m = {}; meta.categories.forEach((c) => (m[c.key] = c.label)); return m;
  }, [meta]);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">Prodotti</h1>
          <p className="mt-1 text-sm text-steel">{count} risultati</p>
        </div>
        <div className="flex items-center gap-2">
          <a href={CSV_EXPORT_URL} data-testid="products-export-csv-button">
            <Button variant="secondary" size="sm"><Download className="h-4 w-4" /> Esporta CSV</Button>
          </a>
          <Button variant="lime" size="sm" onClick={() => { setNewName(""); setNewOpen(true); }} data-testid="products-new-button">
            <Plus className="h-4 w-4" /> Nuovo prodotto
          </Button>
        </div>
      </div>

      {/* Filtri */}
      <div className="flex flex-col gap-3 rounded-xl border border-line bg-surface2 p-3 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-tertiary" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per nome o SKU"
            className="pl-9"
            data-testid="products-search-input"
          />
        </div>
        <Select value={brand} onValueChange={setBrand}>
          <SelectTrigger className="w-full sm:w-[150px]" data-testid="products-brand-filter"><SelectValue placeholder="Marchio" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i marchi</SelectItem>
            {meta.brands.map((b) => <SelectItem key={b.key} value={b.key}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-full sm:w-[150px]" data-testid="products-category-filter"><SelectValue placeholder="Categoria" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le categorie</SelectItem>
            {meta.categories.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full sm:w-[140px]" data-testid="products-status-filter"><SelectValue placeholder="Stato" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="PUBLISHED">Pubblicati</SelectItem>
            <SelectItem value="DRAFT">Bozze</SelectItem>
          </SelectContent>
        </Select>
        <Select value={stock} onValueChange={setStock}>
          <SelectTrigger className="w-full sm:w-[130px]" data-testid="products-stock-filter"><SelectValue placeholder="Scorte" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le scorte</SelectItem>
            <SelectItem value="out">Esauriti</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="ghost" size="sm" onClick={resetFilters} className="text-steel">Azzera</Button>
      </div>

      {/* Bulk bar */}
      {selected.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-stroke bg-surface3 px-3 py-2" data-testid="products-bulk-bar">
          <span className="text-sm text-chalk">{selected.length} selezionati</span>
          <div className="ml-auto flex items-center gap-2">
            <Select onValueChange={(v) => runBulk("set_category", v)}>
              <SelectTrigger className="h-8 w-[170px]" data-testid="products-bulk-category"><SelectValue placeholder="Assegna categoria" /></SelectTrigger>
              <SelectContent>
                {meta.categories.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" size="sm" disabled={busy} data-testid="products-bulk-actions-menu">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />} Azioni
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Stato</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => runBulk("set_status", "PUBLISHED")}>Pubblica</DropdownMenuItem>
                <DropdownMenuItem onClick={() => runBulk("set_status", "DRAFT")}>Metti in bozza</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>In evidenza</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => runBulk("set_featured", true)}>Metti in evidenza</DropdownMenuItem>
                <DropdownMenuItem onClick={() => runBulk("set_featured", false)}>Togli evidenza</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-destructive" onClick={() => setConfirmDelete(true)}>Elimina</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center justify-between rounded-xl border border-destructive/40 bg-surface2 p-4">
          <p className="text-sm text-chalk">{error}</p>
          <Button variant="secondary" size="sm" onClick={load}>Riprova</Button>
        </div>
      )}

      {/* Desktop table */}
      <div className="hidden overflow-hidden rounded-xl border border-line bg-surface2 md:block">
        <Table data-testid="products-table">
          <TableHeader>
            <TableRow className="border-line hover:bg-transparent">
              <TableHead className="w-10">
                <Checkbox checked={allChecked} onCheckedChange={toggleAll} aria-label="Seleziona tutto" data-testid="products-select-all" />
              </TableHead>
              <TableHead>Prodotto</TableHead>
              <TableHead>Stato</TableHead>
              <TableHead>Da</TableHead>
              <TableHead>Varianti</TableHead>
              <TableHead>Scorte</TableHead>
              <TableHead>Marchio</TableHead>
              <TableHead>Categoria</TableHead>
              <TableHead className="w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!items ? (
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={i} className="border-line"><TableCell colSpan={9}><Skeleton className="h-8 w-full" /></TableCell></TableRow>
              ))
            ) : items.length === 0 ? (
              <TableRow className="border-line"><TableCell colSpan={9} className="py-10 text-center text-sm text-steel">Nessun prodotto trovato. Prova a cambiare filtri.</TableCell></TableRow>
            ) : (
              items.map((p) => (
                <TableRow key={p.id} className="border-line cursor-pointer" onClick={() => navigate(`/admin/prodotti/${p.id}`)}>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={selected.includes(p.id)} onCheckedChange={() => toggleOne(p.id)} aria-label={`Seleziona ${p.name}`} />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Thumb url={p.thumb} name={p.name} />
                      <span className="font-medium text-chalk">{p.name}</span>
                    </div>
                  </TableCell>
                  <TableCell><StatusBadge status={p.status} /></TableCell>
                  <TableCell className="text-steel">{eur(p.min_price)}</TableCell>
                  <TableCell className="text-steel">{p.variant_count}</TableCell>
                  <TableCell className={p.total_qty === 0 ? "text-carrot" : "text-steel"}>{p.total_qty}</TableCell>
                  <TableCell className="text-steel">{p.brand}</TableCell>
                  <TableCell className="text-steel">{catLabel[p.category] || p.category || "—"}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`product-row-actions-${p.id}`}><MoreHorizontal className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/admin/prodotti/${p.id}`)}>Modifica</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => duplicate(p.id)}><Copy className="h-4 w-4" /> Duplica</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Mobile cards */}
      <div className="flex flex-col gap-2 md:hidden" data-testid="products-card-list">
        {!items ? (
          Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)
        ) : items.length === 0 ? (
          <p className="py-8 text-center text-sm text-steel">Nessun prodotto trovato.</p>
        ) : (
          items.map((p) => (
            <div key={p.id} className="flex items-center gap-3 rounded-xl border border-line bg-surface2 p-3">
              <Checkbox checked={selected.includes(p.id)} onCheckedChange={() => toggleOne(p.id)} aria-label={`Seleziona ${p.name}`} />
              <button className="flex min-w-0 flex-1 items-center gap-3 text-left" onClick={() => navigate(`/admin/prodotti/${p.id}`)}>
                <Thumb url={p.thumb} name={p.name} />
                <span className="min-w-0">
                  <span className="block truncate font-medium text-chalk">{p.name}</span>
                  <span className="mt-0.5 flex items-center gap-2 text-xs text-steel">
                    <StatusBadge status={p.status} /> {eur(p.min_price)}
                  </span>
                </span>
              </button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => duplicate(p.id)} aria-label="Duplica"><Copy className="h-4 w-4" /></Button>
            </div>
          ))
        )}
      </div>

      {/* Nuovo prodotto dialog */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nuovo prodotto</DialogTitle></DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-name">Nome</Label>
            <Input id="new-name" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Es. iPhone 15" data-testid="new-product-name-input" autoFocus />
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setNewOpen(false)}>Annulla</Button>
            <Button variant="lime" onClick={createNew} data-testid="new-product-create-button">Crea e continua</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conferma eliminazione bulk */}
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare {selected.length} prodotti?</AlertDialogTitle>
            <AlertDialogDescription>L'operazione non può essere annullata.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:brightness-95" onClick={() => runBulk("delete")}>Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
